import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import { getCameraStreamUrl, type CameraStreamChoice } from '../api/cameraMedia';
import { NativeCameraTransport } from '../api/nativeCameraTransport';
import type { Device } from '../types';
import { useAuth } from './AuthContext';
import { useMonitoring } from './MonitoringContext';

export type CameraConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'error';

export interface CameraStreamSnapshot {
  cameraId: string;
  status: CameraConnectionStatus;
  streamUrl: string | null;
  hasFrame: boolean;
  lastFrameAt: number | null;
  frameWidth: number | null;
  frameHeight: number | null;
  reconnectAttempt: number;
  error: string | null;
}

export interface CameraStreamDescriptor {
  cameraId: string;
  signature: string;
  streamUrl: string | null;
  stream?: CameraStreamChoice;
  frameIntervalMs?: number;
  unavailableMessage?: string;
  preload?: boolean;
}

interface CameraStreamEntry {
  key: string;
  descriptor: CameraStreamDescriptor;
  player: HTMLImageElement | null;
  native: NativeCameraTransport | null;
  video: HTMLVideoElement | null;
  lastFrame: HTMLCanvasElement;
  surfaces: Set<HTMLCanvasElement>;
  visibleSurfaces: Set<HTMLCanvasElement>;
  lastCaptureTick: number;
  lastVideoTime: number;
  videoFrameRequest: number | null;
  snapshot: CameraStreamSnapshot;
  connectionTimeout: number | null;
  nativeTimeout: number | null;
  nativeRetryTimer: number | null;
  nativeRetryAttempt: number;
  reconnectTimer: number | null;
  suspendTimer: number | null;
}

type PlayerFactory = () => HTMLImageElement;

const emptySnapshot: CameraStreamSnapshot = Object.freeze({
  cameraId: '',
  status: 'idle',
  streamUrl: null,
  hasFrame: false,
  lastFrameAt: null,
  frameWidth: null,
  frameHeight: null,
  reconnectAttempt: 0,
  error: null,
});

const reconnectDelays = [1_000, 2_000, 5_000, 10_000, 30_000];
const connectionTimeoutMs = 15_000;
const mainConnectionTimeoutMs = 60_000;
const frameIntervalMs = 16;
const surfaceDetachGraceMs = 900;
const maximumFrameWidth = 1920;
const maximumFrameHeight = 1920;

const withReconnectAttempt = (url: string, attempt: number) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}reconnect=${attempt}`;
};

const streamKey = (cameraId: string, stream: CameraStreamChoice = 'auto') =>
  stream === 'auto' ? cameraId : `${cameraId}:${stream}`;

export const createCameraStreamDescriptor = (
  camera: Device,
  preferredStream: CameraStreamChoice = 'auto',
): CameraStreamDescriptor => {
  const mainReference = camera.camera?.mainStreamSecretReference ?? '';
  const subReference = camera.camera?.subStreamSecretReference ?? '';
  const stream = preferredStream;
  const quality = camera.camera?.streamQuality ?? 'balanced';
  const fps = stream === 'auto' || quality === 'low' ? 3 : quality === 'high' ? 10 : 5;
  // Matches Core DeviceStatusPolicy.IsOperatorControlled; heartbeat failures are not playback policy.
  const isOperatorControlled = [5, 6, 7, 8].includes(camera.backendStatus);
  const canAttemptStream = !camera.camera?.isReservedChannel && !isOperatorControlled;
  const hasConfiguredStream = Boolean(mainReference || subReference) && canAttemptStream;
  const unavailableMessage = camera.camera?.isReservedChannel
    ? 'Резервный канал — камера не подключена'
    : isOperatorControlled ? 'Видеопоток недоступен в текущем состоянии устройства'
      : 'RTSP-поток не настроен';

  return {
    cameraId: camera.id,
    signature: `${camera.id}:${stream}:${mainReference}:${subReference}:${canAttemptStream}:${stream === 'auto' ? 'preview' : stream === 'main' ? 'original' : quality}`,
    streamUrl: hasConfiguredStream ? getCameraStreamUrl(camera.id, stream) : null,
    stream,
    frameIntervalMs: stream === 'main' ? 0 : Math.ceil(1_000 / fps),
    unavailableMessage,
  };
};

export class CameraStreamManager {
  private readonly entries = new Map<string, CameraStreamEntry>();
  private readonly listeners = new Map<string, Set<() => void>>();
  private readonly pendingSurfaces = new Map<string, Set<HTMLCanvasElement>>();
  private frameRequest: number | null = null;
  private preloadsPaused = false;
  private ownerId: string | null | undefined;

  constructor(private readonly createPlayer: PlayerFactory = () => new Image()) {}

  synchronize(descriptors: CameraStreamDescriptor[], ownerId?: string | null) {
    if (ownerId !== undefined && ownerId !== this.ownerId) {
      this.ownerId = ownerId;
      this.dispose();
    }
    const nextDescriptors = new Map(descriptors.map((descriptor) =>
      [streamKey(descriptor.cameraId, descriptor.stream), descriptor]));

    for (const cameraId of this.entries.keys()) {
      if (!nextDescriptors.has(cameraId)) this.stop(cameraId);
    }

    for (const descriptor of descriptors) {
      const existing = this.entries.get(streamKey(descriptor.cameraId, descriptor.stream));
      if (!existing) {
        this.createEntry(descriptor);
        continue;
      }

      if (
        existing.descriptor.signature !== descriptor.signature ||
        existing.descriptor.streamUrl !== descriptor.streamUrl ||
        existing.descriptor.preload !== descriptor.preload
      ) {
        this.replaceStream(existing, descriptor);
      }
    }
  }

  isOwner(ownerId: string | undefined) {
    return this.ownerId === ownerId;
  }

  setPreloadsPaused(paused: boolean) {
    if (this.preloadsPaused === paused) return;
    this.preloadsPaused = paused;
    for (const entry of this.entries.values()) {
      if (!entry.descriptor.preload) continue;
      if (paused) this.suspendEntry(entry);
      else this.activateEntry(entry);
    }
  }

  subscribe(cameraId: string, listener: () => void, stream: CameraStreamChoice = 'auto') {
    cameraId = streamKey(cameraId, stream);
    const cameraListeners = this.listeners.get(cameraId) ?? new Set<() => void>();
    cameraListeners.add(listener);
    this.listeners.set(cameraId, cameraListeners);

    return () => {
      cameraListeners.delete(listener);
      if (!cameraListeners.size) this.listeners.delete(cameraId);
    };
  }

  getSnapshot(cameraId: string, stream: CameraStreamChoice = 'auto'): CameraStreamSnapshot {
    return this.entries.get(streamKey(cameraId, stream))?.snapshot ?? emptySnapshot;
  }

  attachSurface(cameraId: string, surface: HTMLCanvasElement, stream: CameraStreamChoice = 'auto') {
    cameraId = streamKey(cameraId, stream);
    const entry = this.entries.get(cameraId);
    if (!entry) {
      const surfaces = this.pendingSurfaces.get(cameraId) ?? new Set<HTMLCanvasElement>();
      surfaces.add(surface);
      this.pendingSurfaces.set(cameraId, surfaces);
      return () => {
        surfaces.delete(surface);
        const current = this.entries.get(cameraId);
        if (current) this.detachSurface(current, surface);
        if (!surfaces.size) this.pendingSurfaces.delete(cameraId);
      };
    }

    if (entry.suspendTimer !== null) {
      window.clearTimeout(entry.suspendTimer);
      entry.suspendTimer = null;
    }
    entry.surfaces.add(surface);
    entry.visibleSurfaces.add(surface);
    if (entry.snapshot.hasFrame && entry.snapshot.status === 'live') this.captureEntry(entry, true);
    this.paintSurface(entry, surface);
    this.activateEntry(entry);
    return () => this.detachSurface(entry, surface);
  }

  setSurfaceVisible(cameraId: string, surface: HTMLCanvasElement, visible: boolean,
    stream: CameraStreamChoice = 'auto') {
    const entry = this.entries.get(streamKey(cameraId, stream));
    if (!entry?.surfaces.has(surface)) return;
    if (visible) {
      const becameVisible = !entry.visibleSurfaces.has(surface);
      entry.visibleSurfaces.add(surface);
      if (becameVisible) this.captureEntry(entry, true);
    } else entry.visibleSurfaces.delete(surface);
  }

  reconnect(cameraId: string, stream: CameraStreamChoice = 'auto') {
    cameraId = streamKey(cameraId, stream);
    const entry = this.entries.get(cameraId);
    if (!entry?.descriptor.streamUrl) return;

    this.clearTimers(entry);
    entry.snapshot = {
      ...entry.snapshot,
      status: 'reconnecting',
      reconnectAttempt: entry.snapshot.reconnectAttempt + 1,
      error: null,
    };
    this.notify(cameraId);
    if (entry.player) {
      if (entry.native) this.fallbackToMjpeg(entry, entry.native);
      this.connect(entry);
      this.scheduleNativeRetry(entry);
    }
    else {
      this.closePlayers(entry);
      this.activateEntry(entry);
    }
  }

  stop(cameraId: string) {
    const entry = this.entries.get(cameraId);
    if (!entry) return;

    this.clearTimers(entry);
    this.closePlayers(entry);
    entry.surfaces.clear();
    entry.visibleSurfaces.clear();
    this.pendingSurfaces.delete(cameraId);
    this.entries.delete(cameraId);
    this.notify(cameraId);
    this.stopFramePumpIfIdle();
  }

  dispose() {
    for (const cameraId of [...this.entries.keys()]) this.stop(cameraId);
    this.pendingSurfaces.clear();
    if (this.frameRequest !== null) {
      this.cancelFrame(this.frameRequest);
      this.frameRequest = null;
    }
  }

  private createEntry(descriptor: CameraStreamDescriptor) {
    const lastFrame = document.createElement('canvas');
    lastFrame.width = 1;
    lastFrame.height = 1;
    const key = streamKey(descriptor.cameraId, descriptor.stream);
    const pendingSurfaces = this.pendingSurfaces.get(key);
    const entry: CameraStreamEntry = {
      key,
      descriptor,
      player: null,
      native: null,
      video: null,
      lastFrame,
      surfaces: new Set(pendingSurfaces),
      visibleSurfaces: new Set(pendingSurfaces),
      lastCaptureTick: -Infinity,
      lastVideoTime: -1,
      videoFrameRequest: null,
      snapshot: {
        cameraId: descriptor.cameraId,
        status: descriptor.streamUrl ? 'idle' : 'error',
        streamUrl: descriptor.streamUrl,
        hasFrame: false,
        lastFrameAt: null,
        frameWidth: null,
        frameHeight: null,
        reconnectAttempt: 0,
        error: descriptor.streamUrl ? null : descriptor.unavailableMessage ?? 'RTSP-поток не настроен',
      },
      connectionTimeout: null,
      nativeTimeout: null,
      nativeRetryTimer: null,
      nativeRetryAttempt: 0,
      reconnectTimer: null,
      suspendTimer: null,
    };
    this.pendingSurfaces.delete(key);
    this.entries.set(key, entry);
    this.notify(key);
    if (this.isActive(entry)) this.activateEntry(entry);
  }

  private replaceStream(entry: CameraStreamEntry, descriptor: CameraStreamDescriptor) {
    this.clearTimers(entry);
    this.closePlayers(entry);
    entry.descriptor = descriptor;
    entry.lastFrame.width = 1;
    entry.lastFrame.height = 1;

    if (!descriptor.streamUrl) {
      entry.snapshot = {
        cameraId: descriptor.cameraId,
        status: 'error',
        streamUrl: null,
        hasFrame: false,
        lastFrameAt: null,
        frameWidth: null,
        frameHeight: null,
        reconnectAttempt: 0,
        error: descriptor.unavailableMessage ?? 'RTSP-поток не настроен',
      };
      this.notify(entry.key);
      return;
    }

    entry.snapshot = {
      cameraId: descriptor.cameraId,
      status: this.isActive(entry) ? 'connecting' : 'idle',
      streamUrl: descriptor.streamUrl,
      hasFrame: false,
      lastFrameAt: null,
      frameWidth: null,
      frameHeight: null,
      reconnectAttempt: 0,
      error: null,
    };
    if (this.isActive(entry)) this.activateEntry(entry);
    this.notify(entry.key);
  }

  private isActive(entry: CameraStreamEntry) {
    return entry.descriptor.preload ? !this.preloadsPaused : entry.surfaces.size > 0;
  }

  private activateEntry(entry: CameraStreamEntry, retryNative = false) {
    if ((entry.player && !retryNative) || entry.native || !entry.descriptor.streamUrl || !this.isActive(entry)) return;
    const nativeAvailable = typeof RTCPeerConnection !== 'undefined' ||
      Boolean(document.createElement('video').canPlayType('application/vnd.apple.mpegurl'));
    if (nativeAvailable) {
      if (!entry.player) {
        entry.snapshot = {
          ...entry.snapshot,
          status: entry.snapshot.hasFrame ? 'reconnecting' : 'connecting',
          error: null,
        };
        this.notify(entry.key);
      }
      const native = new NativeCameraTransport(entry.descriptor.cameraId, entry.descriptor.stream ?? 'auto',
        () => this.fallbackToMjpeg(entry, native));
      entry.native = native;
      entry.nativeTimeout = window.setTimeout(() => this.fallbackToMjpeg(entry, native),
        entry.descriptor.stream === 'main' ? mainConnectionTimeoutMs : connectionTimeoutMs);
      void native.start().then((mode) => {
        if (entry.native !== native || !this.isActive(entry)) { native.stop(); return; }
        if (!mode) { this.fallbackToMjpeg(entry, native); return; }
        entry.video = native.video;
        this.watchVideoFrames(entry);
        this.ensureFramePump();
      }).catch(() => this.fallbackToMjpeg(entry, native));
      return;
    }
    this.startMjpeg(entry);
  }

  private fallbackToMjpeg(entry: CameraStreamEntry, native: NativeCameraTransport) {
    if (entry.native !== native) return;
    if (entry.videoFrameRequest !== null) entry.video?.cancelVideoFrameCallback?.(entry.videoFrameRequest);
    entry.videoFrameRequest = null;
    native.stop();
    entry.native = null;
    entry.video = null;
    entry.lastVideoTime = -1;
    if (entry.nativeTimeout !== null) window.clearTimeout(entry.nativeTimeout);
    entry.nativeTimeout = null;
    if (this.isActive(entry)) {
      this.startMjpeg(entry);
      this.scheduleNativeRetry(entry);
    }
  }

  private scheduleNativeRetry(entry: CameraStreamEntry) {
    if (entry.native || entry.nativeRetryTimer !== null || !this.isActive(entry) ||
      !(typeof RTCPeerConnection !== 'undefined' ||
        document.createElement('video').canPlayType('application/vnd.apple.mpegurl'))) return;
    const delay = Math.min(300_000, 30_000 * 2 ** Math.min(entry.nativeRetryAttempt++, 4));
    entry.nativeRetryTimer = window.setTimeout(() => {
      entry.nativeRetryTimer = null;
      this.activateEntry(entry, true);
    }, delay);
  }

  private startMjpeg(entry: CameraStreamEntry) {
    if (entry.player || !entry.descriptor.streamUrl) return;
    entry.player = this.createPlayer();
    entry.snapshot = {
      ...entry.snapshot,
      status: entry.snapshot.hasFrame ? 'reconnecting' : 'connecting',
      error: null,
    };
    this.configurePlayer(entry);
    this.connect(entry);
    this.ensureFramePump();
  }

  private detachSurface(entry: CameraStreamEntry, surface: HTMLCanvasElement) {
    entry.surfaces.delete(surface);
    entry.visibleSurfaces.delete(surface);
    if (entry.descriptor.preload || entry.surfaces.size || entry.suspendTimer !== null) return;
    entry.suspendTimer = window.setTimeout(() => {
      entry.suspendTimer = null;
      if (!entry.surfaces.size) this.suspendEntry(entry);
    }, surfaceDetachGraceMs);
  }

  private suspendEntry(entry: CameraStreamEntry) {
    if (!entry.player && !entry.native) return;
    this.clearTimers(entry);
    this.closePlayers(entry);
    entry.snapshot = {
      ...entry.snapshot,
      status: 'idle',
      error: null,
    };
    this.notify(entry.key);
    this.stopFramePumpIfIdle();
  }

  private closePlayers(entry: CameraStreamEntry) {
    if (entry.nativeTimeout !== null) window.clearTimeout(entry.nativeTimeout);
    entry.nativeTimeout = null;
    if (entry.videoFrameRequest !== null) entry.video?.cancelVideoFrameCallback?.(entry.videoFrameRequest);
    entry.videoFrameRequest = null;
    entry.lastCaptureTick = -Infinity;
    entry.lastVideoTime = -1;
    entry.nativeRetryAttempt = 0;
    if (entry.player) {
      entry.player.onload = null;
      entry.player.onerror = null;
      entry.player.src = '';
      entry.player = null;
    }
    entry.native?.stop();
    entry.native = null;
    entry.video = null;
  }

  private configurePlayer(entry: CameraStreamEntry) {
    if (!entry.player) return;

    entry.player.crossOrigin = 'use-credentials';
    entry.player.decoding = 'async';
    entry.player.onload = () => this.captureEntry(entry, true);
    entry.player.onerror = () => this.scheduleReconnect(entry, 'Соединение с камерой прервано');
  }

  private connect(entry: CameraStreamEntry) {
    if (!entry.player || !entry.descriptor.streamUrl) return;

    if (entry.connectionTimeout !== null) window.clearTimeout(entry.connectionTimeout);
    entry.snapshot = {
      ...entry.snapshot,
      status: entry.snapshot.hasFrame ? 'reconnecting' : 'connecting',
      error: null,
    };
    this.notify(entry.key);
    entry.player.src = withReconnectAttempt(
      entry.descriptor.streamUrl,
      entry.snapshot.reconnectAttempt,
    );
    entry.connectionTimeout = window.setTimeout(
      () => this.scheduleReconnect(entry, 'Камера не ответила вовремя'),
      entry.descriptor.stream === 'main' ? mainConnectionTimeoutMs : connectionTimeoutMs,
    );
  }

  private scheduleReconnect(entry: CameraStreamEntry, message: string) {
    if (!this.entries.has(entry.key) || entry.reconnectTimer !== null) return;

    if (entry.connectionTimeout !== null) {
      window.clearTimeout(entry.connectionTimeout);
      entry.connectionTimeout = null;
    }
    const nextAttempt = entry.snapshot.reconnectAttempt + 1;
    const delay = reconnectDelays[Math.min(nextAttempt - 1, reconnectDelays.length - 1)];
    entry.snapshot = {
      ...entry.snapshot,
      status: entry.snapshot.hasFrame ? 'reconnecting' : 'error',
      reconnectAttempt: nextAttempt,
      error: message,
    };
    this.notify(entry.key);
    entry.reconnectTimer = window.setTimeout(() => {
      entry.reconnectTimer = null;
      this.connect(entry);
    }, delay);
  }

  private ensureFramePump() {
    if (this.frameRequest !== null) return;
    this.frameRequest = this.requestFrame(() => this.frameTick());
  }

  private frameTick() {
    this.frameRequest = null;
    for (const entry of this.entries.values()) {
      if (entry.videoFrameRequest !== null && entry.lastVideoTime >= 0) continue;
      this.captureEntry(entry);
    }

    if ([...this.entries.values()].some((entry) => entry.player || entry.video)) {
      this.frameRequest = this.requestFrame(() => this.frameTick());
    }
  }

  private watchVideoFrames(entry: CameraStreamEntry) {
    const video = entry.video;
    if (!video?.requestVideoFrameCallback) return;
    entry.videoFrameRequest = video.requestVideoFrameCallback(() => {
      if (entry.video !== video) return;
      entry.videoFrameRequest = null;
      this.captureEntry(entry, true);
      this.watchVideoFrames(entry);
    });
  }

  private captureEntry(entry: CameraStreamEntry, newFrame = false) {
    // Wait for the first presented native frame when video callbacks are available.
    const video = entry.video && (entry.videoFrameRequest === null || entry.lastVideoTime >= 0) &&
      entry.video.readyState >= 2 &&
      entry.video.videoWidth > 0 && entry.video.videoHeight > 0
      ? entry.video : null;
    const player = video ?? entry.player;
    const sourceWidth = video?.videoWidth ?? entry.player?.naturalWidth ?? 0;
    const sourceHeight = video?.videoHeight ?? entry.player?.naturalHeight ?? 0;
    if (!player || !this.isActive(entry) || (entry.reconnectTimer !== null && !video) || sourceWidth <= 0 || sourceHeight <= 0) return;
    if (video) {
      if (entry.nativeTimeout !== null) window.clearTimeout(entry.nativeTimeout);
      entry.nativeTimeout = null;
      entry.nativeRetryAttempt = 0;
    }
    // Cache the first native frame and release MJPEG even when its card is offscreen.
    if (entry.snapshot.hasFrame && entry.snapshot.status === 'live' && !entry.visibleSurfaces.size &&
      !(video && entry.player)) return;
    const tick = performance.now();
    if (!video && !newFrame && entry.descriptor.stream !== 'main' &&
      tick - entry.lastCaptureTick < (entry.descriptor.frameIntervalMs ?? 334)) return;
    if (video && !newFrame && entry.lastVideoTime === video.currentTime) return;
    entry.lastCaptureTick = tick;
    if (video) entry.lastVideoTime = video.currentTime;
    const firstFrame = !entry.snapshot.hasFrame;

    const scale = video || entry.descriptor.stream === 'main' ? 1 : Math.min(
      1,
      maximumFrameWidth / sourceWidth,
      maximumFrameHeight / sourceHeight,
    );
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    if (entry.lastFrame.width !== width || entry.lastFrame.height !== height) {
      entry.lastFrame.width = width;
      entry.lastFrame.height = height;
    }

    try {
      const context = entry.lastFrame.getContext('2d', { alpha: false });
      if (!context) return;
      context.drawImage(player, 0, 0, width, height);
    } catch {
      if (entry.native) this.fallbackToMjpeg(entry, entry.native);
      else this.scheduleReconnect(entry, 'Браузер не смог прочитать кадр камеры');
      return;
    }

    if (video) {
      if (entry.nativeTimeout !== null) window.clearTimeout(entry.nativeTimeout);
      entry.nativeTimeout = null;
      if (entry.player) {
        entry.player.onload = null;
        entry.player.onerror = null;
        entry.player.src = '';
        entry.player = null;
      }
    }

    if (entry.connectionTimeout !== null) {
      window.clearTimeout(entry.connectionTimeout);
      entry.connectionTimeout = null;
    }
    if (entry.reconnectTimer !== null) {
      window.clearTimeout(entry.reconnectTimer);
      entry.reconnectTimer = null;
    }
    const frameCapturedAt = Date.now();
    const connectionStateChanged =
      !entry.snapshot.hasFrame ||
      entry.snapshot.status !== 'live' ||
      entry.snapshot.reconnectAttempt !== 0 ||
      entry.snapshot.error !== null;
    const frameDimensionsChanged =
      entry.snapshot.frameWidth !== width ||
      entry.snapshot.frameHeight !== height;
    if (connectionStateChanged || frameDimensionsChanged) {
      entry.snapshot = {
        ...entry.snapshot,
        status: 'live',
        hasFrame: true,
        lastFrameAt: frameCapturedAt,
        frameWidth: width,
        frameHeight: height,
        reconnectAttempt: 0,
        error: null,
      };
    } else {
      // Frame painting is deliberately independent from React. Keep the
      // snapshot object stable while live so canvas updates do not cause React renders.
      entry.snapshot.lastFrameAt = frameCapturedAt;
    }
    for (const surface of firstFrame ? entry.surfaces : entry.visibleSurfaces) this.paintSurface(entry, surface);
    if (connectionStateChanged || frameDimensionsChanged) this.notify(entry.key);
  }

  private paintSurface(entry: CameraStreamEntry, surface: HTMLCanvasElement) {
    if (!entry.snapshot.hasFrame) return;
    if (
      surface.width !== entry.lastFrame.width ||
      surface.height !== entry.lastFrame.height
    ) {
      surface.width = entry.lastFrame.width;
      surface.height = entry.lastFrame.height;
    }
    const context = surface.getContext('2d', { alpha: false });
    context?.drawImage(entry.lastFrame, 0, 0);
  }

  private notify(cameraId: string) {
    for (const listener of this.listeners.get(cameraId) ?? []) listener();
  }

  private clearTimers(entry: CameraStreamEntry) {
    if (entry.connectionTimeout !== null) window.clearTimeout(entry.connectionTimeout);
    if (entry.reconnectTimer !== null) window.clearTimeout(entry.reconnectTimer);
    if (entry.suspendTimer !== null) window.clearTimeout(entry.suspendTimer);
    if (entry.nativeTimeout !== null) window.clearTimeout(entry.nativeTimeout);
    if (entry.nativeRetryTimer !== null) window.clearTimeout(entry.nativeRetryTimer);
    entry.connectionTimeout = null;
    entry.reconnectTimer = null;
    entry.suspendTimer = null;
    entry.nativeTimeout = null;
    entry.nativeRetryTimer = null;
  }

  private stopFramePumpIfIdle() {
    if (
      this.frameRequest !== null &&
      ![...this.entries.values()].some((entry) => entry.player || entry.video)
    ) {
      this.cancelFrame(this.frameRequest);
      this.frameRequest = null;
    }
  }

  private requestFrame(callback: FrameRequestCallback) {
    if (typeof window.requestAnimationFrame === 'function') return window.requestAnimationFrame(callback);
    return window.setTimeout(() => callback(performance.now()), frameIntervalMs);
  }

  private cancelFrame(handle: number) {
    if (typeof window.cancelAnimationFrame === 'function') { window.cancelAnimationFrame(handle); return; }
    window.clearTimeout(handle);
  }
}

const globalManagerKey = '__korsaCameraStreamManager';
const managerHost = globalThis as typeof globalThis & {
  [globalManagerKey]?: CameraStreamManager;
};
const globalCameraStreamManager =
  managerHost[globalManagerKey] ??= new CameraStreamManager();

const StreamManagerContext = createContext(globalCameraStreamManager);

export function StreamManagerScope({
  manager,
  children,
}: {
  manager: CameraStreamManager;
  children: ReactNode;
}) {
  return (
    <StreamManagerContext.Provider value={manager}>
      {children}
    </StreamManagerContext.Provider>
  );
}

export function StreamManagerProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, profile } = useAuth();
  const { devices } = useMonitoring();
  const descriptors = useMemo(
    () => isAuthenticated
      ? devices
          .filter((device) => device.type === 'camera')
          .flatMap((camera) => [{ ...createCameraStreamDescriptor(camera), preload: true },
            createCameraStreamDescriptor(camera, 'main')])
      : [],
    [devices, isAuthenticated],
  );

  const ownerId = isAuthenticated ? profile?.id : null;
  useLayoutEffect(() => {
    globalCameraStreamManager.setPreloadsPaused(document.visibilityState !== 'visible');
    globalCameraStreamManager.synchronize(descriptors, ownerId);
  }, [descriptors, ownerId]);

  useEffect(() => {
    const handlePageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) globalCameraStreamManager.dispose();
    };
    const handleVisibility = () => globalCameraStreamManager.setPreloadsPaused(document.visibilityState !== 'visible');
    const handlePageShow = () => {
      handleVisibility();
      globalCameraStreamManager.synchronize(descriptors, ownerId);
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [descriptors, ownerId]);

  return (
    <StreamManagerScope manager={globalCameraStreamManager}>
      {children}
    </StreamManagerScope>
  );
}

/** Ready means every eligible preview has an actual decoded frame or an honest connection error. */
export function useCameraPreviewsReady(): boolean {
  const manager = useContext(StreamManagerContext);
  const { devices, loadState } = useMonitoring();
  const { profile } = useAuth();
  const cameraIds = useMemo(() => devices.filter(device => device.type === 'camera' &&
    createCameraStreamDescriptor(device).streamUrl !== null).map(device => device.id), [devices]);
  const subscribe = useCallback((listener: () => void) => {
    const unsubscribe = cameraIds.map(cameraId => manager.subscribe(cameraId, listener));
    return () => unsubscribe.forEach(stop => stop());
  }, [cameraIds, manager]);
  const getSnapshot = useCallback(() => manager.isOwner(profile?.id) && (loadState === 'error' ||
    (loadState !== 'idle' && loadState !== 'loading' && cameraIds.every(cameraId => {
      const snapshot = manager.getSnapshot(cameraId);
      return (snapshot.hasFrame && snapshot.status === 'live') || snapshot.status === 'error';
    }))), [cameraIds, loadState, manager, profile?.id]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useManagedCameraStream(cameraId: string, active = true,
  stream: CameraStreamChoice = 'auto'): {
  snapshot: CameraStreamSnapshot;
  surfaceRef: RefObject<HTMLCanvasElement | null>;
  reconnect: () => void;
} {
  const manager = useContext(StreamManagerContext);
  const surfaceRef = useRef<HTMLCanvasElement>(null);
  const subscribe = useCallback(
    (listener: () => void) => manager.subscribe(cameraId, listener, stream),
    [cameraId, manager, stream],
  );
  const getSnapshot = useCallback(
    () => manager.getSnapshot(cameraId, stream),
    [cameraId, manager, stream],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || !active) return;

    let detach: (() => void) | undefined;
    let inViewport = true;
    const setConnected = (connected: boolean) => {
      detach?.();
      detach = undefined;
      if (connected && document.visibilityState === 'visible') {
        detach = manager.attachSurface(cameraId, surface, stream);
        manager.setSurfaceVisible(cameraId, surface, inViewport, stream);
      }
    };
    // Surfaces follow routes; auto preloads continue until the provider pauses a hidden tab.
    const handleVisibility = () => setConnected(document.visibilityState === 'visible');
    handleVisibility();
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(
      ([entry]) => {
        inViewport = Boolean(entry?.isIntersecting);
        manager.setSurfaceVisible(cameraId, surface, inViewport, stream);
      },
      { threshold: 0.01 },
    );
    observer?.observe(surface);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      observer?.disconnect();
      detach?.();
    };
  }, [active, cameraId, manager, stream]);

  const reconnect = useCallback(() => manager.reconnect(cameraId, stream), [cameraId, manager, stream]);
  return { snapshot, surfaceRef, reconnect };
}
